import { Box, Container, Typography } from '@mui/material';
import PageTitle from '../components/coreLayout/PageTitle';
import Seo from '../components/coreLayout/Seo';
import FleetItemDisplay from '../components/features/FleetItemDisplay';

export interface Facts {
    readonly speed: string;
    readonly seats: string;
    readonly power: string;
}

export interface FleetImage {
    readonly src: string;
    readonly fallback: string;
    readonly alt: string;
}

export interface FleetItem {
    readonly name: string;
    readonly description: string;
    readonly keyFacts: Facts;
    readonly images: FleetImage[];
}

const fleetPageSx = {
    page: {
        background: 'linear-gradient(180deg, #ffffff 0%, rgba(255, 183, 137, 0.07) 100%)',
    },
    intro: {
        maxWidth: 760,
        margin: '0 auto',
        textAlign: 'center',
        paddingTop: { xs: '4px', sm: '8px' },
        paddingBottom: { xs: '8px', sm: '16px' },
        paddingLeft: { xs: '16px', sm: 0 },
        paddingRight: { xs: '16px', sm: 0 },
    },
    introText: {
        color: 'text.soft',
        fontSize: { xs: '1rem', sm: '1.1rem' },
        lineHeight: 1.6,
        fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
    },
    list: {
        paddingTop: { xs: '16px', sm: '24px' },
        paddingBottom: { xs: '32px', sm: '64px' },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '32px', sm: '56px' },
    },
} as const;

const fleet: FleetItem[] = [
    {
        name: 'Cessna 206',
        description: `A reliable and versatile aircraft used in commercial roles the world over.
            GSL Aviation has operated the 206 since 2011, on both floats and wheels.
            It seats up to 5 passengers with guaranteed window seats, and the rear doors can be removed for photography and filming.`,
        keyFacts: {
            speed: '130 knots',
            seats: 'Up to 5 passengers',
            power: '300hp IO-550 Continental Engine',
        },
        images: [
            {
                src: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cairnsTourScenicFlightAircraft.webp',
                fallback:
                    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cairnsTourScenicFlightAircraft.JPG',
                alt: 'Cessna 206 at Cairns Airport, ready to depart on a scenic flight',
            },
            {
                src: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cessna206CairnsScenicFlights.webp',
                fallback:
                    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cessna206CairnsScenicFlights.JPG',
                alt: 'Cairns scenic flight aircraft. Cessna 206 operated by GSL Aviation',
            },
        ],
    },
    {
        name: 'GA8 Airvan',
        description: `A dependable workhorse, manufactured right here in Australia.
            GSL Aviation has operated the GA8 for more than a decade.
            Fitted with a cargo pod it's ideal for island hops and freight, with guaranteed window seats and great visibility for passengers too.`,
        keyFacts: {
            speed: '110 knots',
            seats: 'Up to 7 passengers',
            power: '300hp IO-540 Lycoming Engine',
        },
        images: [
            {
                src: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/airvanAirlieBeachTour.webp',
                fallback:
                    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/airvanAirlieBeachTour.jpg',
                alt: 'GA8 Airvan on a GSL Aviation scenic charter flight over the Whitsundays',
            },
            {
                src: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/airvanWhitsundayScenicFlight.webp',
                fallback:
                    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/airvanWhitsundayScenicFlight.jpg',
                alt: 'GA8 Airvan on charter with GSL Aviation',
            },
        ],
    },
    {
        name: 'Cessna Grand Caravan',
        description: `A powerful, versatile turbo-prop that seats up to 13 — with a window seat for every passenger.
            In a freight configuration it carries full pallets through the rear cargo door.
            Known for its reliability and safety, the Caravan handles scenic flights, passenger transport and freight charter alike.`,
        keyFacts: {
            speed: '150 knots',
            seats: 'Up to 13 passengers',
            power: '675hp PT6 turbo-prop',
        },
        images: [
            {
                src: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/aircraftCharterCairns.webp',
                fallback:
                    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/aircraftCharterCairns.JPG',
                alt: 'Cessna Grand Caravan on a scenic charter flight over the Whitsundays',
            },
            {
                src: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cairnsCharterAircraftCapeYork.webp',
                fallback:
                    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cairnsCharterAircraftCapeYork.JPG',
                alt: 'The Cessna Caravan Aircraft on Charter Cape York, ex Cairns',
            },
            {
                src: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cairnsFreightAircraftCharter.webp',
                fallback:
                    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cairnsFreightAircraftCharter.jpg',
                alt: 'Cessna Caravan airfcraft loading freight on charter from Cairns',
            },
            {
                src: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cessnaCaravanWhitsundayScenicFlight.webp',
                fallback:
                    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/aircraft/cessnaCaravanWhitsundayScenicFlight.jpg',
                alt: 'Cessna Grand Caravan in flight over the Whitsundays on charter with GSL Aviation',
            },
        ],
    },
];

export default function FleetPage() {
    return (
        <Box sx={fleetPageSx.page}>
            <Seo
                title="Our Fleet | Cairns Scenic Flight & Charter Aircraft | GSL Aviation"
                description="Meet the GSL Aviation fleet — Cessna 206, GA8 Airvan and Cessna Grand Caravan — flying scenic flights, air charter and freight from Cairns across North Queensland."
                path="/fleet"
            />
            <Box sx={{ backgroundColor: 'white' }}>
                <Container maxWidth="lg">
                    <PageTitle title="Our Fleet" tagline="Your ride to adventure in North Qld" compact />
                </Container>
            </Box>
            <Container maxWidth="lg">
                <Box sx={fleetPageSx.intro}>
                    <Typography sx={fleetPageSx.introText}>
                        Every aircraft in the GSL Aviation fleet is maintained to the highest standard and chosen for
                        the job — from doors-off photography in the Cessna 206 to full-pallet freight in the Grand
                        Caravan. Whatever the mission, we have the right wings for it.
                    </Typography>
                </Box>
                <Box sx={fleetPageSx.list}>
                    {fleet.map((item, i) => (
                        <FleetItemDisplay key={item.name} asset={item} reverse={i % 2 === 1} />
                    ))}
                </Box>
            </Container>
        </Box>
    );
}
